import React, { FC } from 'react'
import { LinkButton } from '@/atoms/Button'
import { FlexColumn } from '@/atoms/FlexContainers'
import { Image } from '@/atoms/Image'
import { ParagraphLG, TitleMD } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useThemeContext } from '@/contexts/ThemeContext'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

interface Props {
  projectName: string
  projectSlug: string
}

const exclusiveSceneData = {
  [slugs.sight]: {
    slug: slugs.sight,
    href: '/m/exclusive/sight',
    imageUrl: '/v1716503346/SIGHT/scenes_all_sight.webp',
  },
  [slugs.soundOfHopeTheStoryOfPossumTrot]: {
    slug: slugs.soundOfHopeTheStoryOfPossumTrot,
    href: '/m/exclusive/sound-of-hope-the-story-of-possum-trot',
    imageUrl: '/v1719952701/Possum%20Trot/Theatrical/soh_exclusive-scene_thumb.webp',
  },
  [slugs.bonhoeffer]: {
    slug: slugs.bonhoeffer,
    href: `/m/exclusive/${slugs.bonhoeffer}`,
    imageUrl: '/v1732121310/bonhoeffer/bonhoeffer-exclusive-scene-img-02.webp',
  },
  [slugs.kingOfKings]: {
    slug: slugs.kingOfKings,
    href: `/m/exclusive/${slugs.kingOfKings}`,
    imageUrl: '/v1744242726/king-of-kings/TKOK_16x9_v2_BTSThumbnail.webp',
  },
}

export const ExpressExclusiveScene: FC<Props> = ({ projectSlug, projectName }) => {
  const { t } = useTranslate('pif')
  const { isDarkMode } = useThemeContext()
  const exclusiveScene = exclusiveSceneData[projectSlug]

  if (!exclusiveScene) return null

  const isKingOfKings = projectSlug === slugs.kingOfKings

  return (
    <FlexColumn className="gap-3">
      <TitleMD color={isDarkMode ? 'white' : 'core-gray-950'} weight="semibold">
        {isKingOfKings
          ? t('yourExclusiveDigitalAssetsExpressKoK', 'Your Exclusive Bonus Scene and Digital Assets!')
          : t('yourExclusiveSceneTitle', 'Your Exclusive Scenes!')}
      </TitleMD>
      <ParagraphLG weight="normal" color={isDarkMode ? 'white' : 'core-gray-950'}>
        {isKingOfKings ? (
          <Translate i18nKey="yourExclusiveDigitalAssetsDescriptionKoK" t={t} values={{ projectName }}>
            We&apos;ve prepared an exclusive bonus scene and digital assets from {{ projectName }}, including a
            printable movie poster and coloring pages. Click the button below to access them now!
          </Translate>
        ) : (
          <Translate i18nKey="yourExclusiveSceneDescriptionSoundOfHopeV4" t={t} values={{ projectName }}>
            We&apos;ve emailed you an exclusive early access scene from {{ projectName }}, allowing you to enjoy it
            before anyone else. Simply click the button below to start watching now!
          </Translate>
        )}
      </ParagraphLG>
      {exclusiveScene.imageUrl && (
        <a
          className="relative block rounded-lg"
          style={{ aspectRatio: 16 / 9 }}
          href={exclusiveScene.href}
          target="_blank"
        >
          <Image src={exclusiveScene.imageUrl} alt={projectName ?? ''} fill />
        </a>
      )}
      <LinkButton
        href={exclusiveScene.href}
        target="_blank"
        variant={isDarkMode ? 'oxide-bright' : 'oxide-primary'}
        className="mt-2"
      >
        {isKingOfKings
          ? t('viewExclusiveAssetsWatchKoK', 'Watch and Download Now')
          : t('watchExclusiveScene', 'Watch Exclusive Scene')}
      </LinkButton>
    </FlexColumn>
  )
}
